/**
 * src/components/PayrollClaimsPanel.tsx
 * 管理者専用：月次給与申告確認パネル
 *
 * - アシスタントの給与申告一覧を表示
 * - 申告内容 vs 勤怠実績 vs monthly_payments の比較
 * - ステータス更新 / 管理者メモ / 確定額
 * - 給与確認文の自動生成・コピー
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { api } from '../api/client'
import { normalizeImageUrl } from './reportUtils'

// ----------------------------------------------------------------
// 型定義
// ----------------------------------------------------------------
type Claim = {
  id: string
  assistant_id: string
  assistant_name: string
  assistant_daily_rate: number
  assistant_overtime_hourly: number
  assistant_email?: string | null
  target_month: string
  full_days: string
  overtime_total_min: number
  overtime_details: string
  transport_total: number
  transport_notes: string | null
  other_total: number
  other_notes: string | null
  other_expense_receipt_urls?: string | null
  remarks: string | null
  status: 'submitted' | 'reviewed' | 'confirmed'
  submitted_at: string | null
  admin_notes: string | null
  confirmed_amount: number | null
  confirmed_base_pay: number | null
  confirmed_overtime_pay: number | null
  confirmed_transport_total: number | null
  confirmed_other_expense_total: number | null
  confirmed_other_allowance_total: number | null
  attendance_days: number
  attendance_overtime_min: number
  attendance_part_time_min: number
  attendance_transport: number
  payment_total_amount: number | null
  payment_status: string | null
}

type EmailLog = {
  id: number
  assistant_id: string
  target_month: string
  to_email: string
  subject: string
  status: 'sent' | 'failed'
  error_message: string | null
  sent_at: string
  email_type: 'confirmation' | 'reminder'
}

// ── 給与申告未提出者リマインド ──────────────────────────────────
type ReminderTarget = {
  assistant_id: string
  name: string
  email: string | null
  can_send: boolean
  is_test_email: boolean
}
type ReminderPreview = {
  month: string
  closed: boolean
  total_missing: number
  can_send: number
  no_email_names: string[]
  test_email_names: string[]
  targets: ReminderTarget[]
}
type ReminderSendResult = {
  success: boolean
  sent: number
  skipped: number
  sent_names: string[]
  skipped_names: string[]
  results: { assistant_id: string; name: string; success: boolean; reason?: string }[]
  message?: string
}

// ----------------------------------------------------------------
// ユーティリティ
// ----------------------------------------------------------------
function fmt(n: number | null | undefined) { return (n ?? 0).toLocaleString('ja-JP') }
function calcMinutePay(minutes: number, hourly: number): number {
  return Math.round((Number(minutes ?? 0) * Number(hourly ?? 0)) / 60)
}

function parseJson<T>(s: string | null | undefined, fallback: T): T {
  if (!s) return fallback
  try { return JSON.parse(s) as T } catch { return fallback }
}

// ----------------------------------------------------------------
// ステータスバッジ
// ----------------------------------------------------------------
function ClaimStatusBadge({ status }: { status: string }) {
  const map: Record<string, [string, string]> = {
    submitted: ['提出済み', 'badge-amber'],
    reviewed:  ['確認済み', 'badge-blue'],
    confirmed: ['確定',     'badge-green'],
  }
  const [label, cls] = map[status] ?? [status, 'badge-gray']
  return <span className={`badge ${cls}`}>{label}</span>
}

// ----------------------------------------------------------------
// 差分チップ
// ----------------------------------------------------------------
function DiffChip({ match, claimVal, actualVal }: {
  match: boolean; claimVal: string; actualVal: string
}) {
  return (
    <span style={{
      fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 600,
      background: match ? 'var(--green-light)'                 : 'var(--amber-light)',
      color:      match ? 'var(--green)'            : 'var(--amber)',
      border:     `1px solid ${match ? 'var(--green-border)' : 'var(--amber-border)'}`,
      whiteSpace: 'nowrap' as const,
    }}>
      {match ? '✓ 一致' : `⚠ 申告:${claimVal} / 実績:${actualVal}`}
    </span>
  )
}

// ----------------------------------------------------------------
// 給与確認文生成
// ----------------------------------------------------------------
function generateSalaryText(claim: Claim, confirmedAmt: number | null): string {
  const fullDays: string[]                            = parseJson(claim.full_days, [])
  const details: { date: string; minutes: number }[]  = parseJson(claim.overtime_details, [])
  const partTimeDet: { date: string; minutes: number; note?: string }[] = parseJson(
    (claim as any).part_time_details ?? '[]', []
  )
  const [y, m] = claim.target_month.split('-').map(Number)
  const monthStr = `${y}年${m}月`

  const overtimeMin  = claim.overtime_total_min ?? 0
  const overtimeH    = (overtimeMin / 60).toFixed(1)
  const ptMin        = Number((claim as any).part_time_total_min ?? 0) || partTimeDet.reduce((sum, d) => sum + Number(d.minutes ?? 0), 0)
  const ptH          = (ptMin / 60).toFixed(1)
  const basePay      = fullDays.length * claim.assistant_daily_rate
  const ptPay        = calcMinutePay(ptMin, claim.assistant_overtime_hourly)
  const overtimePay  = calcMinutePay(overtimeMin, claim.assistant_overtime_hourly)
  const transport    = claim.transport_total ?? 0
  const other        = claim.other_total ?? 0
  const total        = confirmedAmt ?? (basePay + ptPay + overtimePay + transport + other)

  const daysList = [...fullDays].sort()
    .map((d) => `${Number(d.slice(5, 7))}/${Number(d.slice(8))}`)
    .join('、')

  let t = `${claim.assistant_name}さん\n\n`
  t += `お疲れ様です。\n`
  t += `${monthStr}分の給与について、下記の内容で確認をお願いいたします。\n\n`
  t += `【対象月】${monthStr}分\n`
  t += `・フルタイム勤務日数：${fullDays.length}日\n`
  if (daysList) t += `（${daysList}）\n`
  t += `\n・日給：${fmt(claim.assistant_daily_rate)}円 × ${fullDays.length}日 = ${fmt(basePay)}円\n`

  t += `\n・フルタイム以外の勤務：${ptH}時間\n`
  if (ptMin > 0) {
    t += `　- ${ptMin}分 ÷ 60分 × ${fmt(claim.assistant_overtime_hourly)}円 = ${fmt(ptPay)}円\n`
    if (partTimeDet.length > 0) {
      for (const d of partTimeDet) {
        const [, dm, dd] = d.date.split('-').map(Number)
        t += `　（${dm}/${dd}日 ${d.minutes}分${d.note ? ' ' + d.note : ''}）\n`
      }
    }
  }

  t += `\n・残業時間合計：${overtimeH}時間\n`
  if (overtimeMin > 0) {
    t += `　- ${overtimeMin}分 ÷ 60分 × ${fmt(claim.assistant_overtime_hourly)}円 = ${fmt(overtimePay)}円\n`
    if (details.length > 0) {
      for (const d of details) {
        const [, dm, dd] = d.date.split('-').map(Number)
        t += `　（${dm}/${dd}日 ${d.minutes}分）\n`
      }
    }
  }
  t += `\n・交通費：${fmt(transport)}円\n`
  if (claim.transport_notes) t += `（${claim.transport_notes}）\n`
  t += `\n・その他経費：${fmt(other)}円\n`
  if (other > 0 && claim.other_notes) t += `（${claim.other_notes}）\n`
  t += `\n・合計支給額：${fmt(total)}円\n`
  t += `\n内容に誤りがある場合のみご返信ください。\n`
  t += `問題なければ返信不要です。\n\nよろしくお願いいたします。`

  return t
}

// ================================================================
// 申告詳細（展開時の内側）
// ================================================================
function ClaimDetail({ claim, onUpdated }: {
  claim: Claim
  onUpdated: () => void
}) {
  const fullDays: string[]                            = parseJson(claim.full_days, [])
  const details: { date: string; minutes: number }[]  = parseJson(claim.overtime_details, [])
  const partTimeDet: { date: string; minutes: number; note?: string }[] = parseJson(
    (claim as any).part_time_details ?? '[]', []
  )
  const overtimeMin  = claim.overtime_total_min ?? 0
  const overtimeH    = (overtimeMin / 60).toFixed(1)
  const ptMin        = Number((claim as any).part_time_total_min ?? 0) || partTimeDet.reduce((sum, d) => sum + Number(d.minutes ?? 0), 0)
  const ptH          = (ptMin / 60).toFixed(1)
  const basePay      = fullDays.length * claim.assistant_daily_rate
  const ptPay        = calcMinutePay(ptMin, claim.assistant_overtime_hourly)
  const overtimePay  = calcMinutePay(overtimeMin, claim.assistant_overtime_hourly)
  // part_time を含めた概算合計
  const conceptTotal = basePay + ptPay + overtimePay + claim.transport_total + (claim.other_total ?? 0)
  const attendancePartTimeMin = Number((claim as any).attendance_part_time_min ?? 0)
  const attendancePartTimeH   = (attendancePartTimeMin / 60).toFixed(1)
  const ptDiff                = ptMin !== attendancePartTimeMin

  const [adminNotes,   setAdminNotes]   = useState(claim.admin_notes ?? '')
  const [confirmedAmt, setConfirmedAmt] = useState<string>(
    claim.confirmed_amount != null ? String(claim.confirmed_amount) : ''
  )

  // 項目別確定値（内訳）。全て空欄なら従来通り confirmed_amount のみの互換動作。
  // ★ 未確定（confirmed_base_pay 等が未保存）かつ status がまだ 'confirmed' でない場合のみ、
  //   本人申告値（＋現在把握できている実額）から入力補助用の初期値を提示する。
  //   これはあくまで入力の出発点であり、保存操作をして初めてDBへ反映される。
  //   既に status='confirmed' の（主にmigration以前の）legacyデータは、この推測値で
  //   勝手に埋めない ＝ 何も入力していない空欄のまま表示し、黙ってbackfillされることを防ぐ。
  //   その他手当等（課税）に対応する既存の入力元は無いため、推測初期値は常に 0 とする。
  const canSuggestItemized = claim.status !== 'confirmed'
  const suggested = {
    basePay:         basePay + ptPay,
    overtimePay:     overtimePay,
    transport:       claim.transport_total,
    otherExpense:    claim.other_total ?? 0,
    otherAllowance:  0,
  }
  const initItemized = (saved: number | null, suggestedValue: number) =>
    saved != null ? String(saved) : (canSuggestItemized ? String(suggestedValue) : '')

  const [confirmedBasePay,       setConfirmedBasePay]       = useState(initItemized(claim.confirmed_base_pay, suggested.basePay))
  const [confirmedOvertimePay,   setConfirmedOvertimePay]   = useState(initItemized(claim.confirmed_overtime_pay, suggested.overtimePay))
  const [confirmedTransport,     setConfirmedTransport]     = useState(initItemized(claim.confirmed_transport_total, suggested.transport))
  const [confirmedOtherExpense,  setConfirmedOtherExpense]  = useState(initItemized(claim.confirmed_other_expense_total, suggested.otherExpense))
  const [confirmedOtherAllowance, setConfirmedOtherAllowance] = useState(initItemized(claim.confirmed_other_allowance_total, suggested.otherAllowance))
  const itemizedIsSuggested = canSuggestItemized && claim.confirmed_base_pay == null

  const itemizedInputs = [
    confirmedBasePay, confirmedOvertimePay, confirmedTransport, confirmedOtherExpense, confirmedOtherAllowance,
  ]
  const itemizedAnyFilled = itemizedInputs.some((v) => v.trim() !== '')
  const itemizedAllFilled = itemizedInputs.every((v) => v.trim() !== '')
  const itemizedSum = itemizedInputs.reduce((s, v) => s + (Number(v.trim()) || 0), 0)
  const itemizedConfirmedAmt = confirmedAmt.trim() === '' ? conceptTotal : Number(confirmedAmt.trim())
  const itemizedMismatch = itemizedAllFilled && itemizedSum !== itemizedConfirmedAmt
  const [generatedText, setGeneratedText] = useState('')
  const [saving,  setSaving]  = useState(false)
  const [copied,  setCopied]  = useState(false)

  // ★ useRef で常に最新の generatedText を保持（クロージャの stale 問題を防ぐ）
  const generatedTextRef = useRef(generatedText)
  useEffect(() => { generatedTextRef.current = generatedText }, [generatedText])

  // textarea の onChange: state と ref を両方更新
  const handleGeneratedTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setGeneratedText(e.target.value)
    generatedTextRef.current = e.target.value  // 即時反映
  }

  // メール送信関連
  const [emailLogs,      setEmailLogs]      = useState<EmailLog[]>([])
  const [emailSending,   setEmailSending]   = useState(false)
  const [emailConfirm,   setEmailConfirm]   = useState(false)
  const [emailError,     setEmailError]     = useState<string | null>(null)
  const [emailSentToast, setEmailSentToast] = useState(false)

  const isInitialEmail = (e: string | null | undefined) =>
    !e || e.endsWith('@mangaos.test')

  const loadEmailLogs = useCallback(async () => {
    try {
      const res = await api.adminPayrollClaims.emailLogs(claim.target_month)
      // ★ email_type !== 'reminder' … 給与申告リマインドのログはここでは扱わない。
      // 同一テーブルに記録されるため、リマインド送信を「給与確認メール送信済み」として
      // 誤表示してしまわないようにする。
      setEmailLogs(
        (res.logs as EmailLog[]).filter(
          (l) => l.assistant_id === claim.assistant_id && l.email_type !== 'reminder'
        )
      )
    } catch { /* ignore */ }
  }, [claim.assistant_id, claim.target_month])

  useEffect(() => { loadEmailLogs() }, [loadEmailLogs])

  const lastSentLog = emailLogs.find((l) => l.status === 'sent')

  // 件名（確定的に決まるので frontend でも保持）
  const [yy, mm] = claim.target_month.split('-').map(Number)
  const emailSubject = `【給与確認】${yy}年${mm}月分の勤務・給与内容について`

  // 送信ボタン押下 → 本文が未生成なら自動生成してから確認ダイアログを開く
  const handleOpenConfirm = () => {
    setEmailError(null)
    if (!generatedTextRef.current) {
      try {
        const text = generateSalaryText(claim, parseConfirmedAmount())
        setGeneratedText(text)
        generatedTextRef.current = text  // 即時反映
      } catch (ex: any) {
        setEmailError(ex.message ?? '確定支給額の入力を確認してください')
        return
      }
    }
    setEmailConfirm(true)
  }

  // 確認ダイアログから実送信
  // ★ generatedTextRef.current で送信時点の最新本文を確実に取得
  const handleSendEmail = async () => {
    const bodyToSend = generatedTextRef.current

    if (!bodyToSend.trim()) {
      setEmailError('送信する本文がありません。「文面を生成」ボタンで本文を生成してください。')
      return
    }

    const payload = { subject: emailSubject, body: bodyToSend }

    setEmailConfirm(false)
    setEmailSending(true)
    setEmailError(null)
    try {
      await api.adminPayrollClaims.sendEmail(claim.id, payload)
      setEmailSentToast(true)
      setTimeout(() => setEmailSentToast(false), 3000)
      await loadEmailLogs()
    } catch (ex: any) {
      setEmailError(ex.message ?? 'メール送信に失敗しました')
    } finally {
      setEmailSending(false)
    }
  }

  const parseConfirmedAmount = (fallbackToConceptTotal = false): number | null => {
    const raw = confirmedAmt.trim()
    if (raw === '') return fallbackToConceptTotal ? conceptTotal : null

    const n = Number(raw)
    if (!Number.isFinite(n)) {
      throw new Error('確定支給額は数値で入力してください')
    }
    return n
  }

  const parseItemizedField = (raw: string): number | null => {
    const trimmed = raw.trim()
    if (trimmed === '') return null
    const n = Number(trimmed)
    if (!Number.isFinite(n)) throw new Error('内訳の確定値は数値で入力してください')
    return n
  }

  const buildAdminPatch = (status?: 'submitted' | 'reviewed' | 'confirmed') => {
    // 「確定する」時だけは、入力欄が空でも表示上の概算額を確定額として保存する。
    // これにより、placeholder の金額を見てそのまま確定した場合でも confirmed_amount が null にならない。
    const amount = parseConfirmedAmount(status === 'confirmed')

    // 内訳: 一部だけ入力された状態での保存は禁止（全て入力 or 全て空欄のどちらか）。
    if (itemizedAnyFilled && !itemizedAllFilled) {
      throw new Error('内訳（基本給・残業代・交通費・その他経費・その他手当等）は全て入力するか、全て空欄にしてください')
    }

    // ★ 'confirmed' への遷移（新規確定 / reviewedへ戻してからの再確定）は、
    //   項目別内訳5項目を必須とする。「総額だけ確定してattendanceから内訳を推測する」
    //   運用はここで禁止する（migration以前のlegacyデータのみ互換的に内訳なしを許容）。
    if (status === 'confirmed') {
      if (!itemizedAllFilled) {
        throw new Error('確定するには内訳（基本給・残業代・交通費・その他経費・その他手当等）を全て入力してください')
      }
      if (itemizedMismatch) {
        throw new Error(`内訳の合計（¥${fmt(itemizedSum)}）が確定支給額（¥${fmt(itemizedConfirmedAmt)}）と一致しません`)
      }
    }

    // ★ 確定済み申告の確定支給額は空（NULL）にできない（バックエンドも同条件で拒否する）。
    //   ステータス変更を伴わない通常保存で金額欄が空なら、NULLを再送せず入力を促す。
    if (!status && claim.status === 'confirmed' && amount == null) {
      throw new Error('確定済みの申告は確定支給額を空にできません。金額を入力してください')
    }

    return {
      ...(status ? { status } : {}),
      confirmed_amount: amount,
      confirmed_base_pay:              parseItemizedField(confirmedBasePay),
      confirmed_overtime_pay:          parseItemizedField(confirmedOvertimePay),
      confirmed_transport_total:       parseItemizedField(confirmedTransport),
      confirmed_other_expense_total:   parseItemizedField(confirmedOtherExpense),
      confirmed_other_allowance_total: parseItemizedField(confirmedOtherAllowance),
      admin_notes: adminNotes.trim() === '' ? null : adminNotes,
    }
  }

  const update = async (patch: Parameters<typeof api.adminPayrollClaims.update>[1]) => {
    setSaving(true)
    try {
      const res = await api.adminPayrollClaims.update(claim.id, patch)
      // ★ 保存結果（概算額での補完を含む）をstateへ同期し、後続の保存でNULLが再送されないようにする
      const saved = res?.claim
      if (saved) {
        const toStr = (v: number | null | undefined) => (v != null ? String(v) : '')
        setConfirmedAmt(toStr(saved.confirmed_amount))
        setConfirmedBasePay(toStr(saved.confirmed_base_pay))
        setConfirmedOvertimePay(toStr(saved.confirmed_overtime_pay))
        setConfirmedTransport(toStr(saved.confirmed_transport_total))
        setConfirmedOtherExpense(toStr(saved.confirmed_other_expense_total))
        setConfirmedOtherAllowance(toStr(saved.confirmed_other_allowance_total))
      }
      onUpdated()
    } catch (ex: any) {
      alert(`保存に失敗しました: ${ex.message}`)
    } finally { setSaving(false) }
  }

  // ステータス変更時も、現在入力中の confirmed_amount / admin_notes を必ず同時保存する
  const updateWithStatus = (status: 'submitted' | 'reviewed' | 'confirmed') => {
    try {
      update(buildAdminPatch(status))
    } catch (ex: any) {
      alert(ex.message ?? '確定支給額の入力を確認してください')
    }
  }

  const handleSaveAdminFields = () => {
    try {
      update(buildAdminPatch())
    } catch (ex: any) {
      alert(ex.message ?? '確定支給額の入力を確認してください')
    }
  }

  const handleGenerateText = () => {
    try {
      const text = generateSalaryText(claim, parseConfirmedAmount())
      setGeneratedText(text)
      generatedTextRef.current = text  // 即時反映
    } catch (ex: any) {
      alert(ex.message ?? '確定支給額の入力を確認してください')
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedText).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    })
  }

  // 差分チェック
  const daysDiff   = fullDays.length !== claim.attendance_days
  const otDiff     = overtimeMin     !== claim.attendance_overtime_min
  const transDiff  = claim.transport_total !== claim.attendance_transport
  const hasDiff    = daysDiff || otDiff || ptDiff || transDiff

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 16 }}>

      {/* ── 比較テーブル ── */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)',
          letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
          申告 vs 勤怠実績
        </div>
        {hasDiff && (
          <div style={{
            background: 'var(--amber-light)', border: '1px solid var(--amber-border)',
            borderRadius: 6, padding: '8px 12px', marginBottom: 10,
            fontSize: 12, color: 'var(--amber)',
          }}>
            ⚠ 申告内容と勤怠実績に差異があります。確認後に確定額を設定してください。
          </div>
        )}
        <div className="table-wrap">
          <table style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>項目</th>
                <th style={{ textAlign: 'right' }}>本人申告</th>
                <th style={{ textAlign: 'right' }}>勤怠実績</th>
                <th>差分</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ background: daysDiff ? 'var(--amber-light)' : undefined }}>
                <td>フルタイム勤務日数</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{fullDays.length}日</td>
                <td style={{ textAlign: 'right', color: 'var(--ink-2)' }}>{claim.attendance_days}日</td>
                <td><DiffChip match={!daysDiff}
                  claimVal={`${fullDays.length}日`} actualVal={`${claim.attendance_days}日`} /></td>
              </tr>
              <tr style={{ background: otDiff ? 'var(--amber-light)' : undefined }}>
                <td>残業合計</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{overtimeH}h（{overtimeMin}分）</td>
                <td style={{ textAlign: 'right', color: 'var(--ink-2)' }}>
                  {(claim.attendance_overtime_min / 60).toFixed(1)}h（{claim.attendance_overtime_min}分）
                </td>
                <td><DiffChip match={!otDiff}
                  claimVal={`${overtimeMin}分`} actualVal={`${claim.attendance_overtime_min}分`} /></td>
              </tr>
              {(ptMin > 0 || attendancePartTimeMin > 0) && (
                <tr style={{ background: ptDiff ? 'var(--amber-light)' : undefined }}>
                  <td>フルタイム以外の勤務</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{ptH}h（{ptMin}分）</td>
                  <td style={{ textAlign: 'right', color: 'var(--ink-2)' }}>
                    {attendancePartTimeH}h（{attendancePartTimeMin}分）
                  </td>
                  <td><DiffChip match={!ptDiff}
                    claimVal={`${ptMin}分`} actualVal={`${attendancePartTimeMin}分`} /></td>
                </tr>
              )}
              <tr style={{ background: transDiff ? 'var(--amber-light)' : undefined }}>
                <td>交通費</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>¥{fmt(claim.transport_total)}</td>
                <td style={{ textAlign: 'right', color: 'var(--ink-2)' }}>¥{fmt(claim.attendance_transport)}</td>
                <td><DiffChip match={!transDiff}
                  claimVal={`¥${fmt(claim.transport_total)}`} actualVal={`¥${fmt(claim.attendance_transport)}`} /></td>
              </tr>
              <tr style={{ background: (claim.other_total ?? 0) > 0 ? 'var(--paper-2)' : undefined }}>
                <td>その他経費</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                  ¥{fmt(claim.other_total)}
                  {claim.other_notes && (
                    <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 2, fontWeight: 400 }}>
                      {claim.other_notes}
                    </div>
                  )}
                  {(() => {
                    const receiptUrls = parseJson<string[]>(claim.other_expense_receipt_urls, [])
                    if (receiptUrls.length === 0) return null
                    return (
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', marginTop: 4, flexWrap: 'wrap' }}>
                        {receiptUrls.map((url, i) => (
                          <a key={i} href={normalizeImageUrl(url)} target="_blank" rel="noreferrer"
                            title={`領収書 ${i + 1}/${receiptUrls.length}（クリックで拡大）`}>
                            <img src={normalizeImageUrl(url)} alt={`領収書 ${i + 1}`}
                              style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--paper-3)' }} />
                          </a>
                        ))}
                      </div>
                    )
                  })()}
                </td>
                <td style={{ textAlign: 'right', color: 'var(--ink-3)' }}>—</td>
                <td>
                  {(claim.other_total ?? 0) > 0 ? (
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 600,
                      background: 'var(--paper-2)', color: 'var(--ink-3)',
                      border: '1px solid var(--paper-3)', whiteSpace: 'nowrap' as const,
                    }}>
                      申告額に含む
                    </span>
                  ) : (
                    <DiffChip match={true} claimVal="¥0" actualVal="—" />
                  )}
                </td>
              </tr>
              <tr style={{ background: 'var(--paper-2)' }}>
                <td style={{ fontWeight: 600 }}>合計（概算）</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>¥{fmt(conceptTotal)}</td>
                <td style={{ textAlign: 'right', fontSize: 11, color: 'var(--ink-3)' }}>
                  {claim.payment_total_amount != null
                    ? `¥${fmt(claim.payment_total_amount)}（支払準備 / ${claim.payment_status}）`
                    : '支払準備未作成'}
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── 申告詳細 ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* 勤務日一覧 */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 6 }}>
            申告勤務日（{fullDays.length}日）
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {[...fullDays].sort().map((d) => (
              <span key={d} style={{
                fontFamily: 'var(--font-mono)', fontSize: 11,
                background: 'var(--green-light)', color: 'var(--green)',
                border: '1px solid rgba(45,122,58,0.2)',
                borderRadius: 3, padding: '1px 6px',
              }}>
                {d.slice(5).replace('-', '/')}
              </span>
            ))}
          </div>
        </div>

        {/* フルタイム以外の勤務内訳 */}
        {ptMin > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 6 }}>
              フルタイム以外の勤務内訳
            </div>
            {partTimeDet.length === 0 ? (
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>内訳なし（合計のみ）</span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {partTimeDet.map((d, i) => {
                  const [, dm, dd] = d.date.split('-').map(Number)
                  return (
                    <span key={i} style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                      {dm}/{dd}日 {d.minutes}分（{(d.minutes / 60).toFixed(1)}h）{d.note ? ` — ${d.note}` : ''}
                    </span>
                  )
                })}
                <span style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                  合計: {ptH}時間
                </span>
              </div>
            )}
          </div>
        )}

        {/* 残業内訳 */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 6 }}>
            残業内訳
          </div>
          {details.length === 0 ? (
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>—</span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {details.map((d, i) => {
                const [, dm, dd] = d.date.split('-').map(Number)
                return (
                  <span key={i} style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                    {dm}/{dd}日 {d.minutes}分（{(d.minutes / 60).toFixed(1)}h）
                  </span>
                )
              })}
              <span style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                合計: {overtimeH}時間
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 交通費・その他・備考 */}
      {(claim.transport_notes || claim.remarks) && (
        <div style={{ background: 'var(--paper-2)', borderRadius: 6, padding: '10px 14px' }}>
          {claim.transport_notes && (
            <div style={{ fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: 'var(--ink-3)' }}>交通費メモ：</span>{claim.transport_notes}
            </div>
          )}
          {claim.remarks && (
            <div style={{ fontSize: 12 }}>
              <span style={{ color: 'var(--ink-3)' }}>備考：</span>{claim.remarks}
            </div>
          )}
        </div>
      )}

      {/* ── 管理者確認フォーム ── */}
      <div style={{ borderTop: '1px solid var(--paper-3)', paddingTop: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)',
          letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>
          管理者確認
        </div>

        {/* ステータス変更 */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 6 }}>ステータス変更</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {claim.status === 'submitted' && (
              <button className="btn btn-outline btn-sm" disabled={saving}
                onClick={() => updateWithStatus('reviewed')}>
                ✓ 確認済みにする
              </button>
            )}
            {claim.status === 'reviewed' && (
              <>
                <button className="btn btn-primary btn-sm" disabled={saving || itemizedMismatch || !itemizedAllFilled}
                  style={{ background: 'var(--green)', borderColor: 'var(--green)' }}
                  onClick={() => updateWithStatus('confirmed')}>
                  ✓ 確定する
                </button>
                <button className="btn btn-ghost btn-sm" disabled={saving}
                  style={{ color: 'var(--ink-3)', fontSize: 12 }}
                  onClick={() => updateWithStatus('submitted')}>
                  ↩ 差し戻す
                </button>
                {!itemizedAllFilled && (
                  <span style={{ fontSize: 11, color: 'var(--amber)', alignSelf: 'center' }}>
                    確定するには下の内訳を全て入力してください
                  </span>
                )}
              </>
            )}
            {claim.status === 'confirmed' && (
              <button className="btn btn-ghost btn-sm" disabled={saving}
                style={{ color: 'var(--amber)', fontSize: 12 }}
                onClick={() => updateWithStatus('reviewed')}>
                ↩ 確認済みに戻す
              </button>
            )}
          </div>
        </div>

        {/* 確定額 + 管理者メモ */}
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 16, alignItems: 'start' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>確定支給額（円）</div>
            <input type="number" min="0" step="100"
              value={confirmedAmt}
              onChange={(e) => setConfirmedAmt(e.target.value)}
              placeholder={String(conceptTotal)}
              style={{ width: 140, fontSize: 14 }} />
            <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 3 }}>
              空欄のまま「確定する」を押すと概算額（¥{fmt(conceptTotal)}）を確定額として保存します
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 11 }}>管理者メモ（内部用・アシスタントには非表示）</label>
            <textarea value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              style={{ minHeight: 48, fontSize: 13 }}
              placeholder="確認事項・修正指示など" />
          </div>

          <button className="btn btn-outline btn-sm" onClick={handleSaveAdminFields}
            disabled={saving || itemizedMismatch} style={{ alignSelf: 'end', marginBottom: 2 }}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>

        {/* 項目別確定値（内訳）。「確定する」には全項目の入力が必須（新規確定/再確定のみ）。
            monthly_payments生成時には、attendance由来の値ではなくこの内訳がそのまま使われる。 */}
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>
            確定支給額の内訳（確定するには全項目の入力が必須です）
          </div>
          {itemizedIsSuggested && (
            <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 6 }}>
              ↓ 本人申告値から自動入力した参考値です。内容を確認し、必要な箇所だけ修正してください（保存するまでは未確定です）
            </div>
          )}

          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-3)', marginTop: 8, marginBottom: 4 }}>
            課税支給
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 2 }}>基本給</div>
              <input type="number" min="0" step="100" value={confirmedBasePay}
                onChange={(e) => setConfirmedBasePay(e.target.value)}
                style={{ width: '100%', fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 2 }}>時間外手当</div>
              <input type="number" min="0" step="100" value={confirmedOvertimePay}
                onChange={(e) => setConfirmedOvertimePay(e.target.value)}
                style={{ width: '100%', fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 2 }}>その他手当（課税）</div>
              <input type="number" min="0" step="100" value={confirmedOtherAllowance}
                onChange={(e) => setConfirmedOtherAllowance(e.target.value)}
                style={{ width: '100%', fontSize: 13 }} />
              <div style={{ fontSize: 9, color: 'var(--ink-3)', marginTop: 2 }}>
                慰労金・臨時手当・ボーナス等
              </div>
            </div>
          </div>

          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-3)', marginTop: 12, marginBottom: 4 }}>
            非課税・経費支給
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 2 }}>交通費</div>
              <input type="number" min="0" step="100" value={confirmedTransport}
                onChange={(e) => setConfirmedTransport(e.target.value)}
                style={{ width: '100%', fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 2 }}>その他経費（非課税）</div>
              <input type="number" min="0" step="100" value={confirmedOtherExpense}
                onChange={(e) => setConfirmedOtherExpense(e.target.value)}
                style={{ width: '100%', fontSize: 13 }} />
              <div style={{ fontSize: 9, color: 'var(--ink-3)', marginTop: 2 }}>
                業務上の立替・実費精算のみ（賞与等は「その他手当」へ）
              </div>
            </div>
          </div>

          {itemizedAnyFilled && !itemizedAllFilled && (
            <div style={{ fontSize: 11, color: 'var(--amber)', marginTop: 8 }}>
              内訳は全項目入力するか、全て空欄にしてください（一部のみの保存はできません）
            </div>
          )}
          {itemizedMismatch && (
            <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 8 }}>
              内訳の合計（¥{fmt(itemizedSum)}）が確定支給額（¥{fmt(itemizedConfirmedAmt)}）と一致しません。
              一致するまで保存・確定できません。
            </div>
          )}
        </div>
      </div>

      {/* ── 給与確認文生成 ── */}
      <div style={{ borderTop: '1px solid var(--paper-3)', paddingTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)',
            letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            給与確認文の生成
          </span>
          <button className="btn btn-outline btn-sm" onClick={handleGenerateText}>
            文面を生成
          </button>
          {generatedText && (
            <button className="btn btn-primary btn-sm"
              style={{ background: copied ? 'var(--green)' : undefined,
                borderColor: copied ? 'var(--green)' : undefined }}
              onClick={handleCopy}>
              {copied ? '✓ コピー済み' : '📋 コピー'}
            </button>
          )}
        </div>
        {generatedText && (
          <textarea
            value={generatedText}
            onChange={handleGeneratedTextChange}
            style={{
              width: '100%', minHeight: 300, fontSize: 12,
              fontFamily: 'var(--font-mono)', lineHeight: 1.75,
              background: 'var(--paper-2)', border: '1px solid var(--paper-3)',
              borderRadius: 6, padding: '12px 14px', resize: 'vertical',
              boxSizing: 'border-box' as const,
            }}
          />
        )}
        {generatedText && (
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>
            ※ テキストエリア内で直接編集できます。コピー後はメール・LINE などに貼り付けてください。
          </div>
        )}
      </div>

      {/* ── メール送信 ── */}
      <div style={{ borderTop: '1px solid var(--paper-3)', paddingTop: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)',
          letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>
          給与確認メール送信
        </div>

        {/* 宛先メールアドレス表示 */}
        <div style={{ marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>宛先：</span>
          {isInitialEmail(claim.assistant_email) ? (
            <span style={{
              fontSize: 12, color: 'var(--red)', fontWeight: 600,
              background: 'var(--red-light)', padding: '2px 8px', borderRadius: 4,
            }}>
              メールアドレス未登録（送信不可）
            </span>
          ) : (
            <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
              {claim.assistant_email}
            </span>
          )}
        </div>

        {/* 最終送信日時 */}
        {lastSentLog && (
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
              background: 'var(--green-light)', color: 'var(--green)',
              border: '1px solid rgba(45,122,58,0.2)',
            }}>
              ✓ 送信済み
            </span>
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              最終送信：{lastSentLog.sent_at.replace('T', ' ').slice(0, 16)}
            </span>
          </div>
        )}

        {/* 送信エラー表示 */}
        {emailLogs[0]?.status === 'failed' && !lastSentLog && (
          <div style={{
            fontSize: 12, color: 'var(--red)', background: 'var(--red-light)',
            borderRadius: 6, padding: '8px 12px', marginBottom: 10,
          }}>
            ⚠ 前回の送信が失敗しました：{emailLogs[0].error_message}
          </div>
        )}

        {/* 送信成功トースト */}
        {emailSentToast && (
          <div style={{
            fontSize: 12, color: 'var(--green)', background: 'var(--green-light)',
            borderRadius: 6, padding: '8px 12px', marginBottom: 10,
            border: '1px solid rgba(45,122,58,0.2)',
          }}>
            ✓ 確認メールを送信しました。管理者にも送信結果を通知しました。
          </div>
        )}

        {/* 送信エラー（今回の） */}
        {emailError && (
          <div style={{
            fontSize: 12, color: 'var(--red)', background: 'var(--red-light)',
            borderRadius: 6, padding: '8px 12px', marginBottom: 10,
          }}>
            ⚠ {emailError}
          </div>
        )}

        {/* 確認ダイアログ */}
        {emailConfirm && (
          <div style={{
            background: 'var(--paper-2)', border: '1px solid var(--paper-3)',
            borderRadius: 8, padding: '14px 16px', marginBottom: 12,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
              以下の内容でメールを送信します。よろしいですか？
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 3 }}>
              <span style={{ color: 'var(--ink-3)' }}>宛先：</span>
              <strong>{claim.assistant_email}</strong>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 10 }}>
              <span style={{ color: 'var(--ink-3)' }}>件名：</span>
              <strong>{emailSubject}</strong>
            </div>
            {/* 本文プレビュー（送信される実際の本文） */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>
                本文（送信される内容）：
                {generatedText && (
                  <span style={{ marginLeft: 6, color: 'var(--blue)', fontSize: 10 }}>
                    ※「給与確認文の生成」欄で編集した内容が送信されます
                  </span>
                )}
              </div>
              <textarea
                readOnly
                value={generatedText}
                style={{
                  width: '100%', height: 160, fontSize: 11,
                  fontFamily: 'var(--font-mono)', lineHeight: 1.7,
                  background: 'var(--paper)', border: '1px solid var(--paper-3)',
                  borderRadius: 6, padding: '8px 10px', resize: 'none',
                  boxSizing: 'border-box' as const, color: 'var(--ink-2)',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm"
                disabled={emailSending}
                onClick={handleSendEmail}>
                {emailSending ? '送信中...' : '送信する'}
              </button>
              <button className="btn btn-outline btn-sm"
                onClick={() => setEmailConfirm(false)}>
                キャンセル
              </button>
            </div>
          </div>
        )}

        {/* 送信ボタン */}
        {!emailConfirm && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              className="btn btn-outline btn-sm"
              disabled={isInitialEmail(claim.assistant_email) || emailSending}
              onClick={handleOpenConfirm}
              title={isInitialEmail(claim.assistant_email) ? 'メールアドレス未登録' : undefined}
            >
              {emailSending ? '送信中...' : lastSentLog ? '再送する' : 'メール送信'}
            </button>
            {!generatedText && (
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                ※「文面を生成」で本文を確認・編集してから送信できます
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ================================================================
// 給与申告リマインドモーダル
// 既存の「確定シフトをメール送信」モーダル（.overlay/.modal・ステップ形式の結果表示）と
// 「シフト希望未提出の人に催促メッセージ送信」（任意メッセージ・空欄でデフォルト文面）
// の設計をできるだけ流用する。
// ================================================================
function PayrollReminderModal({ month, allTargets, targetIds, onClose, onSent }: {
  month:      string
  allTargets: ReminderTarget[]
  targetIds:  string[]
  onClose:    () => void
  onSent:     () => void
}) {
  const targets  = allTargets.filter((t) => targetIds.includes(t.assistant_id))
  const sendable = targets.filter((t) => t.can_send)
  const unsendable = targets.filter((t) => !t.can_send)

  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [result,  setResult]  = useState<ReminderSendResult | null>(null)
  const [error,   setError]   = useState<string | null>(null)

  const [y, m] = month.split('-').map(Number)
  const defaultPlaceholder =
    `${y}年${m}月分の給与申告がまだ確認できておりません。\n` +
    `給与のお支払い日（毎月10日）に間に合うよう、お早めにMangaOS上から給与申告のご提出をお願いいたします。`

  const handleSend = async () => {
    setSending(true); setError(null)
    try {
      const res = await api.adminPayrollClaims.sendReminder(month, targetIds, message.trim() || undefined)
      setResult(res)
    } catch (ex: any) {
      setError(ex.message ?? '送信に失敗しました')
    } finally { setSending(false) }
  }

  /* ── 送信結果 ── */
  if (result) {
    return (
      <div className="overlay">
        <div className="modal" style={{ maxWidth: 460 }}>
          <div className="modal-header">
            <div className="modal-title">
              {result.skipped === 0 ? '✓ 送信完了' : '送信結果'}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={onSent}>✕</button>
          </div>
          <div className="modal-body">
            <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
              <div style={{ background: 'var(--green-light)', border: '1px solid var(--green-border)', borderRadius: 6, padding: '8px 14px', flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>送信成功</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--green)' }}>{result.sent}名</div>
              </div>
              {result.skipped > 0 && (
                <div style={{ background: 'var(--amber-light)', border: '1px solid var(--amber-border)', borderRadius: 6, padding: '8px 14px', flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>送信できず</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--amber)' }}>{result.skipped}名</div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {result.results.map((r) => (
                <div key={r.assistant_id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: 13, padding: '5px 8px', borderRadius: 5,
                  background: r.success ? 'var(--green-light)' : 'var(--amber-light)',
                }}>
                  <span style={{ fontWeight: 600 }}>{r.name}</span>
                  <span style={{ fontSize: 12, color: r.success ? 'var(--green)' : 'var(--amber)' }}>
                    {r.success ? '✓ 送信済' : `✗ ${r.reason ?? '失敗'}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-primary" onClick={onSent}>閉じる</button>
          </div>
        </div>
      </div>
    )
  }

  /* ── メッセージ入力 ── */
  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <div className="modal-title">給与申告リマインド送信</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 13, marginBottom: 10 }}>
            対象：<strong>{y}年{String(m).padStart(2, '0')}月分</strong>
          </div>

          {sendable.length === 0 ? (
            <div style={{
              background: 'var(--amber-light)', border: '1px solid var(--amber-border)',
              borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 13,
            }}>
              給与申告が未提出の対象者はいません
            </div>
          ) : (
            <div style={{ fontSize: 13, marginBottom: 12 }}>
              送信対象：<strong>{sendable.map((t) => t.name).join('、')}</strong>
            </div>
          )}

          {unsendable.length > 0 && (
            <div style={{
              background: 'var(--amber-light)', border: '1px solid var(--amber-border)',
              borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12,
            }}>
              <span style={{ fontWeight: 600, color: 'var(--amber)' }}>⚠ 送信できません: </span>
              {unsendable.map((t) => `${t.name}（${!t.email ? 'メール未登録' : '仮メール'}）`).join('、')}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>メッセージ（任意・空欄でデフォルト文面）</span>
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={defaultPlaceholder}
              style={{ fontSize: 13, resize: 'vertical' }}
            />
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              ※ 空欄の場合はデフォルト文面で送信します
            </span>
          </div>

          {error && (
            <div style={{
              marginTop: 12, background: 'var(--red-light)', border: '1px solid var(--red-border)',
              borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--red)',
            }}>
              ⚠ {error}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending || sendable.length === 0}>
            {sending
              ? '送信中...'
              : sendable.length === 0
                ? '給与申告が未提出の対象者はいません'
                : `${sendable.length}名に送信`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ================================================================
// 給与申告未提出バナー
// 既存のシフト希望未提出バナー（MissingBanner）と同じ位置づけで、
// 一括リマインド・行ごとの個別リマインド送信ボタンを提供する。
// ================================================================
function MissingPayrollBanner({ month, preview, onReload }: {
  month:    string
  preview:  ReminderPreview | null
  onReload: () => void
}) {
  const [modalTargetIds, setModalTargetIds] = useState<string[] | null>(null)

  if (!preview || preview.total_missing === 0) return null

  return (
    <div style={{
      background: 'var(--amber-light)', border: '1px solid var(--amber-border)',
      borderRadius: 8, padding: '14px 18px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--amber)' }}>
            ！ 給与申告未提出
          </span>
          <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
            {preview.targets.map((t) => t.name).join('、')}
          </span>
        </div>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setModalTargetIds(preview.targets.map((t) => t.assistant_id))}
          disabled={preview.can_send === 0}
          title={preview.can_send === 0 ? '送信できる対象者がいません' : undefined}
        >
          未提出者にリマインド（{preview.can_send}名）
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: 10 }}>
        {preview.targets.map((t) => (
          <div key={t.assistant_id} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '4px 2px', fontSize: 12,
          }}>
            <span style={{ minWidth: 80, fontWeight: 600 }}>{t.name}</span>
            {!t.can_send && (
              <span style={{
                fontSize: 10, background: !t.email ? 'var(--red)' : 'var(--amber)', color: '#fff',
                borderRadius: 10, padding: '1px 6px',
              }}>
                {!t.email ? 'メール未登録' : '仮メール'}
              </span>
            )}
            <button
              className="btn btn-ghost btn-sm"
              style={{ marginLeft: 'auto', fontSize: 11 }}
              disabled={!t.can_send}
              title={!t.can_send ? (!t.email ? 'メール未登録' : '仮メールのため送信不可') : undefined}
              onClick={() => setModalTargetIds([t.assistant_id])}
            >
              リマインド送信
            </button>
          </div>
        ))}
      </div>

      {modalTargetIds && (
        <PayrollReminderModal
          month={month}
          allTargets={preview.targets}
          targetIds={modalTargetIds}
          onClose={() => setModalTargetIds(null)}
          onSent={() => { setModalTargetIds(null); onReload() }}
        />
      )}
    </div>
  )
}

// ================================================================
// メインパネル（Personnel.tsx から呼び出す）
// ================================================================
export function PayrollClaimsPanel({ month }: { month: string }) {
  const [claims,       setClaims]       = useState<Claim[]>([])
  const [loading,      setLoading]      = useState(true)
  const [expandedId,   setExpandedId]   = useState<string | null>(null)
  const [corrections,  setCorrections]  = useState<any[]>([])
  const [corrLoading,  setCorrLoading]  = useState(false)
  const [corrExpanded, setCorrExpanded] = useState(false)
  const [updatingCorr, setUpdatingCorr] = useState<number | null>(null)
  const [corrToast,    setCorrToast]    = useState<{ ok: boolean; msg: string } | null>(null)
  const [corrNotes,    setCorrNotes]    = useState<Record<number, string>>({})
  // ★ 給与申告未提出者リマインド
  const [reminderPreview, setReminderPreview] = useState<ReminderPreview | null>(null)

  const showCorrToast = (ok: boolean, msg: string) => {
    setCorrToast({ ok, msg }); setTimeout(() => setCorrToast(null), 4000)
  }

  // silent: true の場合はローディング表示を出さずに一覧を差し替える。
  // ステータス変更・メール送信など「一覧はすでに表示されている状態からの更新」で使う。
  // これにより、更新のたびにパネル全体が一瞬 LOADING... に置き換わって
  // スクロール位置・展開状態（expandedId）・入力途中の内容が失われる問題を防ぐ。
  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true)
    try {
      const res = await api.adminPayrollClaims.list(month)
      setClaims(res.claims as Claim[])
    } catch {
      if (!opts?.silent) setClaims([])
    } finally {
      if (!opts?.silent) setLoading(false)
    }
  }, [month])

  const refreshClaims = useCallback(() => load({ silent: true }), [load])

  const loadReminderPreview = useCallback(async () => {
    try {
      const res = await api.adminPayrollClaims.reminderTargets(month)
      setReminderPreview(res)
    } catch {
      setReminderPreview(null)
    }
  }, [month])

  const loadCorrections = useCallback(async () => {
    setCorrLoading(true)
    try {
      const res = await api.adminPayrollClaims.corrections.list(month)
      setCorrections(res.corrections ?? [])
    } catch {
      setCorrections([])
    } finally { setCorrLoading(false) }
  }, [month])

  const handleCorrectionAction = async (id: number, action: 'approve' | 'reject' | 'resolve', adminNote?: string) => {
    setUpdatingCorr(id)
    try {
      await api.adminPayrollClaims.corrections.update(id, { action, admin_note: adminNote })
      showCorrToast(true, action === 'approve' ? '修正を反映しました' : action === 'reject' ? '却下しました' : '対応済みにしました')
      await Promise.all([refreshClaims(), loadCorrections()])
    } catch (e: any) {
      showCorrToast(false, e.message ?? 'エラーが発生しました')
    } finally { setUpdatingCorr(null) }
  }

  useEffect(() => {
    load()
    loadCorrections()
    loadReminderPreview()
    setExpandedId(null)
  }, [load, loadCorrections, loadReminderPreview])

  const counts = {
    submitted: claims.filter((c) => c.status === 'submitted').length,
    reviewed:  claims.filter((c) => c.status === 'reviewed').length,
    confirmed: claims.filter((c) => c.status === 'confirmed').length,
  }

  if (loading) return <div className="loading" style={{ padding: 32 }}>LOADING...</div>

  // ★ 給与申告未提出者バナー（一括・個別リマインド送信）。claims が0件でも表示する。
  const missingBanner = (
    <MissingPayrollBanner
      month={month}
      preview={reminderPreview}
      onReload={() => { refreshClaims(); loadReminderPreview() }}
    />
  )

  if (claims.length === 0) {
    return (
      <div>
        {missingBanner}
        <div className="card">
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--ink-3)', fontSize: 13 }}>
            {month} の給与申告は提出されていません
            <div style={{ fontSize: 11, marginTop: 6 }}>
              アシスタントが給与申告を提出すると、ここに表示されます
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      {missingBanner}
      {/* サマリー */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { label: '提出済み（未確認）', count: counts.submitted, color: 'var(--amber)' },
          { label: '確認済み',           count: counts.reviewed,  color: 'var(--blue)'  },
          { label: '確定済み',           count: counts.confirmed, color: 'var(--green)' },
        ].map((s) => (
          <div key={s.label} className="metric" style={{ minWidth: 120, padding: '10px 14px' }}>
            <div className="metric-label">{s.label}</div>
            <div className="metric-value" style={{ color: s.count > 0 ? s.color : 'var(--ink-3)' }}>
              {s.count}名
            </div>
          </div>
        ))}
        <div className="metric" style={{ minWidth: 80, padding: '10px 14px' }}>
          <div className="metric-label">合計</div>
          <div className="metric-value">{claims.length}名</div>
        </div>
      </div>

      {/* 申告リスト */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {claims.map((claim) => {
          const fullDays: string[]  = parseJson(claim.full_days, [])
          const overtimeH           = (claim.overtime_total_min / 60).toFixed(1)
          const isExpanded          = expandedId === claim.id
          const partTimeDet2        = parseJson<{ date: string; minutes: number; note?: string }[]>((claim as any).part_time_details ?? '[]', [])
          const ptMin2              = Number((claim as any).part_time_total_min ?? 0) || partTimeDet2.reduce((sum, d) => sum + Number(d.minutes ?? 0), 0)
          const basePay             = fullDays.length * claim.assistant_daily_rate
          const ptPay2              = calcMinutePay(ptMin2, claim.assistant_overtime_hourly)
          const overtimePay         = calcMinutePay(claim.overtime_total_min, claim.assistant_overtime_hourly)
          const conceptTotal        = basePay + ptPay2 + overtimePay + claim.transport_total + (claim.other_total ?? 0)
          const displayTotal        = claim.confirmed_amount ?? conceptTotal

          const hasDiff = fullDays.length     !== claim.attendance_days
            || claim.overtime_total_min       !== claim.attendance_overtime_min
            || claim.transport_total          !== claim.attendance_transport

          return (
            <div key={claim.id} className="card" style={{
              borderLeft: hasDiff ? '3px solid var(--amber)' : '3px solid transparent',
            }}>
              {/* カードヘッダー */}
              <div
                onClick={() => setExpandedId(isExpanded ? null : claim.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 16px', cursor: 'pointer',
                  borderBottom: isExpanded ? '1px solid var(--paper-2)' : undefined,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 14, minWidth: 80, whiteSpace: 'nowrap' }}>
                  {claim.assistant_name}
                </span>
                <ClaimStatusBadge status={claim.status} />
                {hasDiff && (
                  <span style={{
                    fontSize: 10, color: 'var(--amber)', background: 'var(--amber-light)',
                    border: '1px solid var(--amber-border)',
                    borderRadius: 4, padding: '1px 6px',
                  }}>⚠ 差異あり</span>
                )}
                {claim.confirmed_amount != null && (
                  <span style={{
                    fontSize: 10, color: 'var(--green)', background: 'var(--green-light)',
                    border: '1px solid rgba(45,122,58,0.2)',
                    borderRadius: 4, padding: '1px 6px',
                  }}>確定額あり</span>
                )}

                {/* サマリー数値 */}
                <div style={{ display: 'flex', gap: 14, marginLeft: 'auto', fontSize: 12, color: 'var(--ink-2)', flexWrap: 'wrap' }}>
                  <span>{fullDays.length}日</span>
                  <span>残業 {overtimeH}h</span>
                  <span>交通費 ¥{fmt(claim.transport_total)}</span>
                  <span style={{ fontWeight: 700, color: claim.confirmed_amount != null ? 'var(--green)' : 'var(--ink)' }}>
                    ¥{fmt(displayTotal)}
                    {claim.confirmed_amount != null && <span style={{ fontSize: 10, marginLeft: 3, color: 'var(--green)' }}>確定</span>}
                  </span>
                </div>

                <span style={{
                  fontSize: 16, color: 'var(--ink-3)',
                  transition: 'transform 0.15s',
                  transform: isExpanded ? 'rotate(90deg)' : 'none',
                }}>›</span>
              </div>

              {/* 展開詳細 */}
              {isExpanded && (
                <div style={{ padding: '0 16px 20px' }}>
                  <ClaimDetail claim={claim} onUpdated={refreshClaims} />
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 12, paddingLeft: 2 }}>
        　 支払準備タブから「勤怠から再集計」を実行してください。
      </div>

      {/* ── 修正依頼セクション ── */}
      {corrToast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 9999,
          background: corrToast.ok ? 'var(--green)' : 'var(--red)',
          color: '#fff', borderRadius: 8, padding: '10px 18px', fontSize: 13,
        }}>
          {corrToast.msg}
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <div
          onClick={() => { setCorrExpanded(!corrExpanded); if (!corrExpanded) loadCorrections() }}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
            padding: '10px 14px', background: 'var(--paper-2)', borderRadius: 8,
            border: '1px solid var(--paper-3)',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 13 }}>給与申告 修正依頼</span>
          {corrections.filter((r) => r.status === 'pending').length > 0 && (
            <span style={{
              background: 'var(--red)', color: '#fff', borderRadius: 10,
              fontSize: 11, fontWeight: 700, padding: '1px 7px',
            }}>
              {corrections.filter((r) => r.status === 'pending').length}件
            </span>
          )}
          <span style={{ marginLeft: 'auto', color: 'var(--ink-3)', fontSize: 14 }}>
            {corrExpanded ? '▲' : '▼'}
          </span>
        </div>

        {corrExpanded && (
          <div style={{ marginTop: 8 }}>
            {corrLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>読み込み中...</div>
            ) : corrections.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
                {month} の修正依頼はありません
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {corrections.map((req) => {
                  const orig: Record<string, any> = (() => { try { return JSON.parse(req.original_payload ?? '{}') } catch { return {} } })()
                  const reqd: Record<string, any> = (() => { try { return JSON.parse(req.requested_payload ?? '{}') } catch { return {} } })()
                  // diff_payload: [{label, before, after}] 形式
                  const diffList: Array<{label: string; before: string; after: string}> = (() => {
                    try { return JSON.parse(req.diff_payload ?? '[]') } catch { return [] }
                  })()

                  // 旧形式の場合はorigとreqdから差分を導出して表示
                  const isNewFormat = 'full_days' in orig || 'transport_total' in orig

                  const statusMap: Record<string, [string, string]> = {
                    pending:  ['未対応', 'var(--amber)'],
                    approved: ['反映済み', 'var(--green)'],
                    rejected: ['却下', 'var(--red)'],
                    resolved: ['対応済み', 'var(--ink-3)'],
                  }
                  const [statusLabel, statusColor] = statusMap[req.status] ?? [req.status, 'var(--ink-3)']

                  const corrNote = corrNotes[req.id] ?? ''
                  const setCorrNote = (v: string) => setCorrNotes((prev) => ({ ...prev, [req.id]: v }))

                  return (
                    <div key={req.id} className="card" style={{
                      borderLeft: req.status === 'pending' ? '3px solid var(--amber)' : '3px solid transparent',
                    }}>
                      <div style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{req.assistant_name}</span>
                          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                            {req.target_month.replace('-', '年')}月分
                          </span>
                          <span style={{ fontSize: 11, color: statusColor, fontWeight: 600 }}>
                            [{statusLabel}]
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 'auto' }}>
                            {req.created_at?.slice(0, 16).replace('T', ' ')}
                          </span>
                        </div>

                        {/* 差分表示（新形式: diff_payload、旧形式: 元データとの比較） */}
                        {diffList.length > 0 ? (
                          <div style={{ background: 'var(--paper-2)', borderRadius: 6, padding: '10px 12px', marginBottom: 12 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 8 }}>変更内容</div>
                            {diffList.map((d, i) => (
                              <div key={i} style={{ marginBottom: 6, fontSize: 12 }}>
                                <div style={{ fontWeight: 600, color: 'var(--ink-2)', marginBottom: 2 }}>{d.label}</div>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                  <span style={{ color: 'var(--red)', textDecoration: 'line-through', fontSize: 11 }}>{d.before}</span>
                                  <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>→</span>
                                  <span style={{ color: 'var(--green)', fontWeight: 600, fontSize: 11 }}>{d.after}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : isNewFormat ? (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                            {/* 新形式だが diff_payload なし: orig vs reqd 比較 */}
                            <div style={{ background: 'var(--paper-2)', borderRadius: 6, padding: '10px 12px' }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 6 }}>元の申告</div>
                              {orig.full_days && <div style={{ fontSize: 12 }}>出勤: {Array.isArray(orig.full_days) ? orig.full_days.length : '?'}日</div>}
                              {orig.part_time_total_min != null && <div style={{ fontSize: 12 }}>時間勤務: {(Number(orig.part_time_total_min) / 60).toFixed(1)}h</div>}
                              {orig.transport_total != null && <div style={{ fontSize: 12 }}>交通費: ¥{Number(orig.transport_total).toLocaleString('ja-JP')}</div>}
                              {orig.other_total != null && Number(orig.other_total) > 0 && <div style={{ fontSize: 12 }}>その他: ¥{Number(orig.other_total).toLocaleString('ja-JP')}</div>}
                            </div>
                            <div style={{ background: 'var(--amber-light)', borderRadius: 6, padding: '10px 12px' }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--amber)', marginBottom: 6 }}>修正後</div>
                              {reqd.full_days && <div style={{ fontSize: 12 }}>出勤: {Array.isArray(reqd.full_days) ? reqd.full_days.length : '?'}日</div>}
                              {reqd.part_time_total_min != null && <div style={{ fontSize: 12 }}>時間勤務: {(Number(reqd.part_time_total_min) / 60).toFixed(1)}h</div>}
                              {reqd.transport_total != null && <div style={{ fontSize: 12 }}>交通費: ¥{Number(reqd.transport_total).toLocaleString('ja-JP')}</div>}
                              {reqd.other_total != null && Number(reqd.other_total) > 0 && <div style={{ fontSize: 12 }}>その他: ¥{Number(reqd.other_total).toLocaleString('ja-JP')}</div>}
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                            {/* 旧形式 */}
                            <div style={{ background: 'var(--paper-2)', borderRadius: 6, padding: '10px 12px' }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 6 }}>元の申告</div>
                              <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>（詳細データなし）</div>
                            </div>
                            <div style={{ background: 'var(--amber-light)', borderRadius: 6, padding: '10px 12px' }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--amber)', marginBottom: 6 }}>修正依頼</div>
                              {(reqd as any).requested_transport_total != null && <div style={{ fontSize: 12 }}>交通費: ¥{Number((reqd as any).requested_transport_total).toLocaleString('ja-JP')}</div>}
                              {(reqd as any).requested_part_time_min != null && <div style={{ fontSize: 12 }}>時間勤務: {(Number((reqd as any).requested_part_time_min) / 60).toFixed(1)}h</div>}
                              {(reqd as any).other_description && <div style={{ fontSize: 12 }}>その他: {(reqd as any).other_description}</div>}
                            </div>
                          </div>
                        )}

                        <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: req.status === 'pending' ? 10 : 0 }}>
                          <span style={{ fontWeight: 600 }}>修正理由: </span>{req.reason}
                        </div>
                        {req.admin_note && (
                          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: req.status === 'pending' ? 10 : 0 }}>
                            管理者メモ: {req.admin_note}
                          </div>
                        )}

                        {req.status === 'pending' && (
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input type="text" value={corrNote}
                              onChange={(e) => setCorrNote(e.target.value)}
                              placeholder="管理メモ（任意）" style={{ flex: 1, minWidth: 160, fontSize: 12 }} />
                            <button className="btn btn-sm btn-primary"
                              disabled={updatingCorr === req.id}
                              onClick={() => handleCorrectionAction(req.id, 'approve', corrNote || undefined)}>
                              {updatingCorr === req.id ? '…' : '修正を反映する'}
                            </button>
                            <button className="btn btn-sm btn-outline"
                              disabled={updatingCorr === req.id}
                              onClick={() => handleCorrectionAction(req.id, 'resolve', corrNote || undefined)}>
                              対応済みにする
                            </button>
                            <button className="btn btn-sm btn-ghost"
                              style={{ color: 'var(--red)' }}
                              disabled={updatingCorr === req.id}
                              onClick={() => handleCorrectionAction(req.id, 'reject', corrNote || undefined)}>
                              却下
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}